import type { OfferGroupDataDto, OfferConst } from '@api/types.gen'
import type { PIPE_EVENTS, PipeInterface } from '@render/composables/usePipe'

// Canonically `PIPE_EVENTS.trackingRefresh`. Not value-imported: usePipe already
// reaches this module at runtime (usePipe -> usePipeEventHandlers ->
// usePaymentEvents), so importing the value back closes that cycle and puts the
// constant in TDZ for anyone who later reads it at module scope. The annotation
// pins it to the canonical literal type, so the two cannot drift.
const TRACKING_REFRESH_EVENT: (typeof PIPE_EVENTS)['trackingRefresh'] = 'tracking:refresh-cookies'

// Bus event names for payment-related lifecycle. Kebab-case after the colon
// matches the existing convention (`navigation:show`, `navigation:exit-intent`).
//
// Events that drive an OrderStatus transition in the pipeorders backend are
// listed in `ORDER_STATUS_TRANSITIONS.md` (orders/api). The pipeorders
// integration translates each event into a `transitionOrder(... PUBLIC)` POST.
//
// Status mapping (legacy parity with /workspace/pipelines):
//  - planSelected → PLAN_SELECTED  (note: legacy never set order.status to
//    PLAN_SELECTED; the new system extends legacy by tracking it)
//  - checkoutVisited → VISIT_CHECKOUT  (legacy commonBasicPlanCheckout.js#L145)
//  - checkoutRendered → INITIATE_CHECKOUT  (legacy primer.js#L111 — fires
//    after Primer.showUniversalCheckout resolves; also commonUpsell.js#L140
//    after createPrimerCheckoutSession)
//  - checkoutStarted → SUBMIT_CC_INPUT  (legacy primer.js#L188 — fires from
//    Primer's onPaymentCreationStart; user has just clicked Pay)
//  - checkoutFailed → FAILED_CHECKOUT  (legacy primer.js#L460)
//  - checkoutCompleted → no PUBLIC transition; PURCHASE is webhook-only.
export const PAYMENT_EVENTS = {
  planSelected: 'payment:plan-selected',
  checkoutVisited: 'payment:checkout-visited',
  checkoutRendered: 'payment:checkout-rendered',
  checkoutStarted: 'payment:checkout-started',
  checkoutCompleted: 'payment:checkout-completed',
  checkoutFailed: 'payment:checkout-failed',
  trackingCheckoutRequested: 'tracking:checkout-requested',
  trackingCheckoutLoaded: 'tracking:checkout-loaded',
  trackingCheckoutBeforePaymentCreated: 'tracking:checkout-before-payment-created',
  trackingCheckoutLoadedConfirmation: 'tracking:checkout-loaded-confirmation',
  // Provider-agnostic checkout lifecycle — deliberately separate from the
  // trackingCheckout*/klaviyo+segment-only events above so existing subscribers
  // are untouched. Emitted by BOTH PrimerCheckout and YunoCheckout (tagged via
  // payload.checkoutProvider) so GTM/GA4 can compare the two checkouts.
  checkoutRequested: 'payment:checkout-requested',
  checkoutLoaded: 'payment:checkout-loaded',
  checkoutLoadedConfirmation: 'payment:checkout-loaded-confirmation',
  // Fired alongside the existing Bugsnag abandonment report in usePrimer.ts /
  // useYuno.ts (render was requested but the user left before the card form /
  // payment-method list appeared). Both providers gate on their own
  // `confirmationLoaded`, so the two funnels stay comparable.
  checkoutAbandoned: 'payment:checkout-abandoned',
  // Emitted by the segment_track_custom_event processor; integrations subscribe
  // here instead of coupling directly to the processor handler.
  trackingCustomEvent: 'tracking:custom-event',
  trackingPlanSelectedSlimPayload: 'tracking:plan-selected-slim-payload',
  // Emitted by pipeorders when var.global.email changes to a different non-empty
  // value. Klaviyo and Segment subscribe to clear their identity state so they
  // re-identify against the new address.
  resetUser: 'user:reset',
  // Emitted by pipeorders when its create POST hands back an order the pipe did not
  // have yet: the lead. An abandoned-cart link restoring an existing order is not one.
  orderCreated: 'order:created',
} as const

export type PaymentEventName = (typeof PAYMENT_EVENTS)[keyof typeof PAYMENT_EVENTS]

export interface OrderCreatedPayload {
  orderId: string
}

// Mirror of legacy `payments.js:59-64`'s `trackingProperties` shape — a small
// blob of marketing-attribution signals built from cookies + origin/query
// strings. Used both by the payments-API call (usePaymentData.ts) and by the
// GTM payment events (subscriber in gtm.ts spreads it at top level into the
// dataLayer push, matching how legacy `_push` spread the offer-price
// `trackingProperties`).
//
// NOTE: this is NOT the same as legacy `trackingUtils.getOfferPriceTrackingProperties`
// (which read an opaque user-defined blob off offer-price rows). OfferConst has
// no equivalent field, so we fabricate this payments-API-style shape from the
// data the trackingProperties integration already captures.
/* eslint-disable @typescript-eslint/naming-convention */
export interface LegacyTrackingProperties {
  fb_ads_id: string
  fb_adset_id: string
  cookies: Record<string, string>
  origin_query_string: string
  query_string: string
  // Pipehouse extension (not legacy parity): ingress AB-test trace id
  // captured by seedOriginDefaults in usePipe.ts — `current` = most recent
  // ingress landing's id. Rides along to the payments API
  // (payment_session.trackingProperties) and GTM payment events. `origin` is
  // intentionally omitted from outbound instrumentation.
  current_ingress_trace_id: string
}
/* eslint-enable @typescript-eslint/naming-convention */

export function buildLegacyTrackingProperties(pipe: {
  getData: (key: string) => unknown
}): LegacyTrackingProperties {
  const tp =
    (pipe.getData('var.global.trackingProperties') as
      | { cookies?: Record<string, string>; queryParams?: Record<string, string> }
      | undefined) ?? {}
  const cookies = tp.cookies ?? {}
  const queryParams = tp.queryParams ?? {}
  const originQueryString =
    (pipe.getData('var.system.originQueryString') as string | undefined) ?? ''
  const queryString = typeof window !== 'undefined' ? window.location.search : ''

  /* eslint-disable @typescript-eslint/naming-convention */
  return {
    fb_ads_id: queryParams.fb_ads_id || queryParams.fb_ad_id || '',
    fb_adset_id: queryParams.fb_adset_id || '',
    cookies,
    origin_query_string: originQueryString,
    query_string: queryString,
    current_ingress_trace_id:
      (pipe.getData('var.system.currentIngressTraceId') as string | undefined) ?? '',
  }
  /* eslint-enable @typescript-eslint/naming-convention */
}

export interface PaymentEventPayload {
  ppoId?: string
  paymentType?: OfferConst['paymentType']
  isExitIntent: boolean
  pipelineOfferType?: string

  basePrice?: number
  basePriceWoDiscount?: number
  basePricePerPeriod?: number
  basePriceDiscountPercent?: number
  basePriceDiscountAmount?: number
  basePeriod?: number
  basePeriodUnit?: OfferConst['basePeriodUnit']

  trialPrice?: number
  trialPriceWoDiscount?: number
  trialPricePerPeriod?: number
  trialPriceDiscountPercent?: number
  trialPriceDiscountAmount?: number
  trialPeriod?: number
  trialPeriodUnit?: OfferConst['trialPeriodUnit']

  defaultPlanSelected: boolean
  currency?: string
  orderId?: string
  // Legacy parity: [orderId, pipeId, ppoId].join('-'). Only present when all three are.
  eventId?: string
  // Legacy `payments.js`-shape attribution blob (cookies, fb ad ids, query strings).
  // Subscribers may spread this at top level to match legacy GTM dataLayer pushes.
  trackingProperties: LegacyTrackingProperties
  // Failure-only context.
  reason?: string
  // Which checkout implementation emitted this — only set on the provider-agnostic
  // checkoutRequested/Loaded/LoadedConfirmation/Abandoned events, so GTM/GA4 can
  // compare Primer vs Yuno funnels.
  checkoutProvider?: 'primer' | 'yuno'
  timestamp: number
}

export const resolveActiveVariant = (
  offer: OfferGroupDataDto,
  isExitIntent: boolean,
): OfferConst => {
  if (isExitIntent && offer.exit) return offer.exit
  return offer.regular
}

const resolveTransactionNature = (paymentType?: OfferConst['paymentType']): string => {
  switch (paymentType) {
    case 'one_time':
    case 'gift':
      return 'one time'
    case 'recurring':
    case 'recurring_with_trial':
    default:
      return 'subscription initial'
  }
}

// Must have for data team - applovin etc.
type GTMParams = {
  ppoId: string | null
  ppoTitle: string | null
  price: number | null
  currency: string | null
  defaultPlanSelected: boolean
  transactionNature: string
  ppoUseCase: string
  email: string | null // Needed for ACQ from Kristers
}

export function buildPaymentEventPayload(
  pipe: PipeInterface,
  offer?: OfferGroupDataDto | null,
  extra: {
    reason?: string
    pipelineOfferType?: string
    checkoutProvider?: 'primer' | 'yuno'
  } = {},
  event?: PaymentEventName,
  customData?: Record<string, unknown>,
): PaymentEventPayload | Record<string, unknown> {
  const disallowedExtraParamsEventNames: PaymentEventName[] = [
    PAYMENT_EVENTS.trackingCheckoutRequested,
    PAYMENT_EVENTS.trackingCheckoutLoaded,
    PAYMENT_EVENTS.trackingCheckoutBeforePaymentCreated,
    PAYMENT_EVENTS.trackingCheckoutLoadedConfirmation,
    PAYMENT_EVENTS.trackingCustomEvent,
    PAYMENT_EVENTS.trackingPlanSelectedSlimPayload,
  ]

  if (event && disallowedExtraParamsEventNames.includes(event)) {
    if (customData) {
      return customData
    }

    return {}
  }

  const isExitIntent = !!pipe.getData('var.global.hasExitIntentBeenAccepted')
  // Fall back to the seeded `var.global.selectedOfferGroup` when the caller
  // doesn't pass an offer explicitly — the same source PrimerCheckout already
  // reads its ppoId from. This keeps `payment:checkout-*` payloads useful even
  // when the emitting component doesn't track an offer of its own.
  const resolvedOffer =
    offer ?? (pipe.getData('var.global.selectedOfferGroup') as OfferGroupDataDto | null)
  const variant = resolvedOffer ? resolveActiveVariant(resolvedOffer, isExitIntent) : undefined

  const orderId = (pipe.getData('var.global.orderId') as string | undefined) || undefined
  const pipeId = pipe.data.value?.id
  const ppoId = variant?.ppoId
  const eventId = orderId && pipeId != null && ppoId ? `${orderId}-${pipeId}-${ppoId}` : undefined
  const currency = (pipe.getData('const.system.currency') as string | undefined)?.toUpperCase()
  const selectedOfferGroupIsDefault =
    (pipe.getData('var.global.selectedOfferGroupIsDefault') as boolean | undefined) ?? false
  const defaultPlanSelected =
    extra.pipelineOfferType === undefined || extra.pipelineOfferType === 'BASIC'
      ? selectedOfferGroupIsDefault
      : false

  const gtmParamsRequiredForDataTeam: GTMParams = {
    ppoId: ppoId || null,
    ppoTitle: variant?.ppoTitle ?? null,
    price: variant?.apiBasePrice || null,
    currency: currency || null,
    defaultPlanSelected: defaultPlanSelected,
    transactionNature: resolveTransactionNature(variant?.paymentType),
    ppoUseCase: 'origin',
    email: (pipe.getData('var.global.email') as string) || null,
  }

  return {
    paymentType: variant?.paymentType,
    isExitIntent,

    basePrice: variant?.apiBasePrice,
    basePriceWoDiscount: variant?.basePriceWoDiscount,
    basePricePerPeriod: variant?.basePricePerPeriod,
    basePriceDiscountPercent: variant?.basePriceDiscountPercent,
    basePriceDiscountAmount: variant?.basePriceDiscountAmount,
    basePeriod: variant?.basePeriod,
    basePeriodUnit: variant?.basePeriodUnit,

    trialPrice: variant?.apiTrialPrice,
    trialPriceWoDiscount: variant?.trialPriceWoDiscount,
    trialPricePerPeriod: variant?.trialPricePerPeriod,
    trialPriceDiscountPercent: variant?.trialPriceDiscountPercent,
    trialPriceDiscountAmount: variant?.trialPriceDiscountAmount,
    trialPeriod: variant?.trialPeriod,
    trialPeriodUnit: variant?.trialPeriodUnit,

    orderId,
    eventId,
    trackingProperties: buildLegacyTrackingProperties(pipe),
    reason: extra.reason,
    pipelineOfferType: extra.pipelineOfferType,
    checkoutProvider: extra.checkoutProvider,
    timestamp: Date.now(),
    ...gtmParamsRequiredForDataTeam,
  }
}

export function emitPayment({
  pipe,
  event,
  offer,
  extra = {},
  customData,
}: {
  pipe: PipeInterface
  event: PaymentEventName
  offer?: OfferGroupDataDto | null
  extra?: { reason?: string; pipelineOfferType?: string; checkoutProvider?: 'primer' | 'yuno' }
  customData?: Record<string, unknown>
}) {
  // The payload embeds `buildLegacyTrackingProperties`, whose `cookies` blob the
  // GTM / Klaviyo / Segment subscribers read. Refresh first so every payment
  // event reports the same cookies as the order's rawData and the payment
  // session. Handler runs inline (see PIPE_EVENTS.trackingRefresh).
  pipe.emit(TRACKING_REFRESH_EVENT)
  pipe.emit(event, buildPaymentEventPayload(pipe, offer, extra, event, customData))
}
